import type { ProjectListItem } from '@atlas/api-contract';
import { Link } from '@tanstack/react-router';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { resourceOwnerId, useCan } from '@/features/auth';
import { formatAbsoluteTime, formatRelativeTime } from '@/lib/format/date';

export interface ProjectCardProps {
  project: ProjectListItem;
  onEdit: (project: ProjectListItem) => void;
  onDelete: (project: ProjectListItem) => void;
}

/**
 * One project on the home grid (spec R6.5).
 *
 * The whole card is the link, but only the title is an `<a>`: it stretches over
 * the card with `after:inset-0` instead. That keeps the markup legal — a menu
 * button nested inside an anchor is not — and leaves the keyboard with exactly
 * two stops per card, the project and its menu, rather than a link that
 * swallows everything.
 */
export const ProjectCard = ({ project, onEdit, onDelete }: ProjectCardProps) => {
  const can = useCan();
  /*
   * Edit and delete are owner-or-admin (spec matrix). The menu is hidden for
   * anyone who cannot use either item; the API enforces it regardless.
   */
  const canModify = can.modifyOwned(resourceOwnerId(project));

  return (
    <Card className="relative flex flex-col shadow-none transition-colors hover:border-foreground/20 focus-within:border-foreground/20">
      <CardHeader className="gap-2 pb-4">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">
            <Link
              to="/projects/$projectId"
              params={{ projectId: project.id }}
              className="rounded-sm after:absolute after:inset-0 after:rounded-xl"
            >
              {project.name}
            </Link>
          </CardTitle>

          {canModify ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative z-10 -mr-2 -mt-1 size-8 shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <MoreHorizontal aria-hidden="true" />
                  <span className="sr-only">Actions for {project.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onSelect={() => {
                    onEdit(project);
                  }}
                >
                  <Pencil aria-hidden="true" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => {
                    onDelete(project);
                  }}
                >
                  <Trash2 aria-hidden="true" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <CardDescription className="line-clamp-2 min-h-[2.5rem]">
          {project.description ?? 'No description.'}
        </CardDescription>
      </CardHeader>

    <CardContent className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      <span>
        {project.prototypeCount}{' '}
        {project.prototypeCount === 1 ? 'prototype' : 'prototypes'}
      </span>
      <span aria-hidden="true">·</span>
      <time dateTime={project.updatedAt} title={formatAbsoluteTime(project.updatedAt)}>
        Updated {formatRelativeTime(project.updatedAt)}
      </time>
    </CardContent>
    </Card>
  );
};
